import Page from '../../enums/Page';
import Section from '../../enums/Sections';
import getDeviceInformation from '../../tracking/fraud/getDeviceInformation';
import THGTracker from '../../tracking/thg/THGTracker';
import getContactNumberValidationFunctions
from '../components/address/field-validation/getContactNumberValidationFunctions';
import getDeliveryAddressTypeAttribute from '../delivery-address/getDeliveryAddressTypeAttribute';
import gtmPushCheckoutPageErrors from '../google-tag-manager/gtmPushCheckoutPageErrors';
import gtmPushPaymentOptionUsed from '../google-tag-manager/gtmPushPaymentOptionUsed';
import Translation from '../lang/Translation';
import getPage from '../page/getPage';
import {createApplePaySession} from '../payment-methods/applepay/applepay-api';
import PaymentSectionAttributeReader from '../payment-methods/paymentSectionAttributeReader';
import {isClickAndCollectButtonLayout} from '../selectors/clickAndCollectButtonSelectors';
import {getIsMobileNumber} from '../selectors/ContactNumberSelectors';
import getContactNumberMaxLength from '../selectors/ContactNumberValidationSelectors/getContactNumberMaxLength';
import getContactNumberMinLength from '../selectors/ContactNumberValidationSelectors/getContactNumberMinLength';
import getRegexValidation from '../selectors/ContactNumberValidationSelectors/getRegexValidation';
import getIsStoredAddressValid from '../selectors/DeliveryAddressSelectors/getIsStoredAddressValid';
import {getDeliveryOptionsTrackingAttributes} from '../selectors/DeliveryOptionsSelectors/DeliveryOptionsSelectors';
import {isEmailRequired} from '../selectors/GuestSelectors';
import {isMobile} from '../selectors/DeviceSelectors';
import getSelectedPaymentCategory
from '../selectors/PaymentOptionSelectors/ApmExperimentSelectors/getSelectedPaymentCategory';
import {getCardTrackingAttributes} from '../selectors/PaymentOptionSelectors/getCardTrackingAttributes';
import {
  getContactNumberLengthTrackingAttributes,
  getContactNumberRequiredTrackingAttributes,
  getEmailTrackingAttributes,
  getMarketingPreferencesTrackingAttributes,
} from '../selectors/PaymentOptionSelectors/PaymentOptionSelectors';
import {getPaymentOnlyOrderBody, getPlaceOrderBody} from '../selectors/PlaceOrderSelectors/PlaceOrderSelectors';
import {
  getContactNumber,
  getDeliveryCountryUsedForDeliveryOptions,
  getDeliveryType,
  getSelectedAddressID, getSettings,
  selectIsContactNumberRequired,
} from '../selectors/selectors';
import shouldValidateContactNumberOnPlaceOrder from '../selectors/shouldValidateContactNumberOnPlaceOrder';
import shouldValidateCountryCodeOnPlaceOrder from '../selectors/shouldValidateCountryCodeOnPlaceOrder';
import shouldValidateDeliveryOptionAddressMismatch from '../selectors/shouldValidateDeliveryOptionAddressMismatch';
import doMetric from '../tracking/bigQueryMetricRecorder';
import submitEventDataReader from '../tracking/submitEventDataReader';
import forwardToURL from '../utils/forwardToURL';
import snakeToKebabCase from '../utils/snakeToKebabCase';
import Track from '../utils/tracking';
import V2Validation from '../validation/v2-validation';
import getDeliveryOptionAddressMismatch from './getDeliveryOptionAddressMismatch';
import isBillingAddressBlocked from './isBillingAddressBlocked';
import isDeliveryAddressBlocked from './isDeliveryAddressBlocked';
import {setPageMessage} from './pageMessageActions';
import * as GooglePayActions from './paymentActions/googlePayActions';
import {setRecaptchaToken} from './recaptchaActions';
import {changePayment, resolvePayment, sendOrder} from './sendOrderActions';
import {setApplePaySessionStarted, startOrderProcessing} from './site-actions';

const populateWithDeviceInfo = formData => getDeviceInformation()
  .then(deviceInformation => ({
    ...formData,
    deviceInformation,
  }))
  .catch((error) => {
    Track.track(error);
    return formData;
  });

export const placeOrder = () => (dispatch, getState) => {
  const orderPlaced = {
    Name: 'order',
    OrderId: getState().start.orderNumber,
    URL: window.location.toString(),
    Cur: THGTracker.getCookie('currency_V6'),
    Event: 'placeOrder',
  };
  THGTracker.sendMsg('', JSON.stringify(orderPlaced));
  
  const formData = getPlaceOrderBody(getState());
  
  return populateWithDeviceInfo(formData)
    .then((orderInformation) => {
      dispatch(sendOrder(orderInformation));
    });
};

export const placeResolvePayment = () => (dispatch, getState) => {
  const formData = getPaymentOnlyOrderBody(getState());
  
  return populateWithDeviceInfo(formData)
    .then((resolvePaymentInformation) => {
      dispatch(resolvePayment(resolvePaymentInformation));
    });
};

export const placeChangePayment = () => (dispatch, getState) => {
  const formData = getPaymentOnlyOrderBody(getState());
  
  return populateWithDeviceInfo(formData)
    .then((changePaymentInformation) => {
      dispatch(changePayment(changePaymentInformation));
    });
};

export const trackSubmitOrder = v2FieldsValidationResult => (dispatch, getState) => {
  const errors = submitEventDataReader.getErrors(v2FieldsValidationResult.errors);
  const state = getState();
  const page = getPage();
  const paymentOption = snakeToKebabCase(state.payment.paymentOptionName);
  const paymentMethodCategory = getSelectedPaymentCategory(getState());
  
  let paymentMethodCategoryString = '';
  
  if (paymentMethodCategory && typeof paymentMethodCategory === 'symbol') {
    paymentMethodCategoryString = paymentMethodCategory.description;
  }
  
  const attributes = (state.address?.deliveryAddressList?.addressId || state.pudo.selectedCollectionPoint > -1)
    ? [
      ...PaymentSectionAttributeReader.getPaymentMethodAttribute(paymentOption, paymentMethodCategoryString),
      ...dispatch(getDeliveryAddressTypeAttribute()),
      ...getDeliveryOptionsTrackingAttributes(state),
    ]
    : [
      ...PaymentSectionAttributeReader.getPaymentMethodAttribute(paymentOption, paymentMethodCategoryString),
      ...getDeliveryOptionsTrackingAttributes(state),
    ];
  
  if (state.email && state.email.showMarketingPreferences) {
    attributes.push(getMarketingPreferencesTrackingAttributes(state));
  }
  
  if (page === Page.CHECKOUT) {
    gtmPushCheckoutPageErrors(errors);
  }
  
  try {
    attributes.push(getContactNumberRequiredTrackingAttributes(state));
    attributes.push(getContactNumberLengthTrackingAttributes(state));
    attributes.push(...getEmailTrackingAttributes(state));
    if (state.address?.deliveryAddressList?.addressId) {
      attributes.push(getIsStoredAddressValid(state.address.deliveryAddressList.addressId)(state));
    }
    if (getSettings(state)?.should_log_cv2_value && v2FieldsValidationResult.isValid) {
      attributes.push(...getCardTrackingAttributes(state));
    }
  } catch (error) {
    Track.track(error);
  }
  
  try {
    doMetric('submitOrder', attributes, errors);
  } catch (error) {
    Track.track(error);
  }
};

export const submitOrder = (recaptchaToken = '') => (dispatch, getState) => {
  const state = getState();
  const page = getPage();
  const validationResult = V2Validation.validate();
  const validateEmailTrackingAttributesSetting = getSettings(getState())?.should_validate_email_tracking_attributes_on_place_order;
  
  if (recaptchaToken) {
    dispatch(setRecaptchaToken(recaptchaToken));
  }
  
  if (page === Page.CHECKOUT) {
    dispatch(trackSubmitOrder(validationResult));
    
    if (state.payment.paymentOptionName === 'APPLEPAY') {
      createApplePaySession();
      dispatch(setApplePaySessionStarted(false));
    }
  }
  
  let deliveryAddressBlocked;
  let billingAddressBlocked;
  let deliveryOptionAddressMismatch;
  let isContactNumberValid = true;
  let countryCodeMismatch = false;
  let isValidEmailTrackingAttributes = true;
  
  if (!isClickAndCollectButtonLayout(state) || validationResult.isValid) {
    deliveryAddressBlocked = isDeliveryAddressBlocked(dispatch, getState);
    billingAddressBlocked = isBillingAddressBlocked(dispatch, getState);
  }
  
  if (shouldValidateDeliveryOptionAddressMismatch(state) && validationResult.isValid) {
    deliveryOptionAddressMismatch = getDeliveryOptionAddressMismatch(dispatch, getState);
  }
  
  if (page === Page.CHECKOUT
    && state.ui.sections.some(sectionName => sectionName === Section.ADDRESS)
    && shouldValidateCountryCodeOnPlaceOrder(state) && (getDeliveryType(state) === 'residential')
    && (getSelectedAddressID(state) === '')) {
    const userInputCountryCode = state.address.deliveryAddressList.addresses[''].country;
    const getDeliveryOptionsCountryCode = getDeliveryCountryUsedForDeliveryOptions(state);
    
    if (userInputCountryCode !== getDeliveryOptionsCountryCode) {
      dispatch(setPageMessage(Translation.getText('errors.countryCodeMismatch')));
      try {
        doMetric('submitOrderError',
          [{type: 'country code', label: 'Country Code Validation Error'}],
          'impression');
      } catch (error) {
        Track.track(error);
      }
      countryCodeMismatch = true;
    }
  }
  
  const isContactNumberSettingEnabled = shouldValidateContactNumberOnPlaceOrder(getState());
  
  if (page === Page.CHECKOUT
    && state.ui.sections.some(sectionName => sectionName === Section.ADDRESS || sectionName === Section.CONTACT)
    && isContactNumberSettingEnabled) {
    const contactNumber = getContactNumber(state);
    
    const contactNumberProps = {
      isMobileNumber: getIsMobileNumber(state),
      isRequired: selectIsContactNumberRequired(state),
      minLength: getContactNumberMinLength(state),
      maxLength: getContactNumberMaxLength(state),
      regexValidation: getRegexValidation(state),
    };
    
    const contactNumberValidationFunctions = getContactNumberValidationFunctions(contactNumberProps);
    
    const contactNumberErrors = contactNumberValidationFunctions
      .map(validate => validate(contactNumber))
      .filter(result => result !== undefined);
    
    if (contactNumberErrors?.length > 0) {
      if (validationResult.isValid) {
        dispatch(setPageMessage(Translation.getText('errors.contactNumberInvalid')));
      }
      try {
        doMetric('submitOrderError',
          [{name: 'isValid', value: validationResult.isValid}],
          [{type: 'contact number', label: 'Contact Number Validation Error'}],
          'impression');
      } catch (error) {
        Track.track(error);
      }
      isContactNumberValid = false;
    }
  }
  
  if (page === Page.CHECKOUT && validateEmailTrackingAttributesSetting && isEmailRequired(getState())
    && state.ui.sections.some(sectionName => sectionName === Section.ADDRESS || sectionName === Section.CONTACT)) {
    const emailTrackingAttributes = getEmailTrackingAttributes(state);
    const emailLength = emailTrackingAttributes.find(attr => attr.name === 'emailLength');
    const emailHasAtSymbol = emailTrackingAttributes.find(attr => attr.name === 'emailHasAtSymbol');
    
    if (!emailLength || !emailHasAtSymbol || emailLength.value === 'empty'
      || emailLength.value === 'null' || emailLength.value === 'undefined' || !emailHasAtSymbol.value) {
      if (validationResult.isValid) {
        dispatch(setPageMessage(Translation.getText('errors.emailTrackingAttributesInvalid')));
      }
      try {
        doMetric('submitOrderError',
          [{name: 'isValid', value: validationResult.isValid}],
          [{type: 'email tracking attributes', label: 'Email Tracking Attributes Validation Error'}],
          'impression');
      } catch (error) {
        Track.track(error);
      }
      isValidEmailTrackingAttributes = false;
    }
  }
  
  if (validationResult.isValid && !deliveryAddressBlocked && !billingAddressBlocked
    && !deliveryOptionAddressMismatch && isContactNumberValid && !countryCodeMismatch && isValidEmailTrackingAttributes) {
    if (state.payment.paymentOptionName === 'GOOGLEPAY') {
      if (page === Page.CHECKOUT) {
        dispatch(GooglePayActions.onSubmit());
        return;
      }
    }
    
    if (window.start.settings?.gtm_payment_option_data_enabled && isMobile(getState())) {
      try {
        dispatch(gtmPushPaymentOptionUsed(getState()));
      } catch (error) {
        Track.track(error);
      }
    }
    
    dispatch(startOrderProcessing());
    
    switch (page) {
      case Page.CHECKOUT:
        return dispatch(placeOrder());
      case Page.PAYMENT_RESOLUTION:
        return dispatch(placeResolvePayment());
      case Page.PAYMENT_CHANGE:
        return dispatch(placeChangePayment());
      default:
        dispatch(setPageMessage());
    }
  } else if (recaptchaToken && window.grecaptcha && window.grecaptcha.reset
    && typeof window?.grecaptcha?.reset === 'function') {
    window.grecaptcha.reset();
  }
};

export const cancel = () => (dispatch, getState) => {
  forwardToURL(`${getState().start.siteDataV2.unsecureHomePageUrl}accountHome.secure`);
};
